import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { formatCurrency } from "@/utils/formatCurrency";
import type { CartItem } from "@/types/cart.type";

interface DiscountModalProps {
  isOpen: boolean;
  item: CartItem | null;
  onClose: () => void;
  onSave: (discountRupiah: number) => void; // selalu rupiah
}

export default function DiscountModal({
  isOpen,
  item,
  onClose,
  onSave,
}: DiscountModalProps) {
  // inputType hanya untuk UI, tidak dikirim keluar
  const [inputType, setInputType] = useState<"percent" | "amount">("amount");
  const [discountValue, setDiscountValue] = useState("");

  useEffect(() => {
    if (item) {
      setInputType("amount");
      setDiscountValue(
        item.discount > 0 ? String(item.discount) : "",
      );
    }
  }, [item]);

  if (!isOpen || !item) return null;

  const originalSubtotal = Number(item.sell_price) * item.qty;
  const value = Number(discountValue) || 0;

  let calculatedDiscount = 0;
  let finalSubtotal = originalSubtotal;

  if (value > 0) {
    if (inputType === "percent") {
      calculatedDiscount = originalSubtotal * (value / 100);
    } else {
      calculatedDiscount = value;
    }
    finalSubtotal = originalSubtotal - calculatedDiscount;
  }

  if (finalSubtotal < 0) finalSubtotal = 0;

  const handleSave = () => {
    if (value <= 0) {
      onSave(0);
    } else {
      // Konversi ke rupiah jika persen
      onSave(calculatedDiscount);
    }
  };

  const handleRemoveDiscount = () => {
    onSave(0);
  };

  return (
    <div className="fixed inset-0 z-[999999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-[#37393d] rounded-2xl p-6 w-[90%] max-w-md shadow-2xl z-10 animate-in fade-in zoom-in duration-200">
        <h2 className="mb-1 text-xl font-bold text-gray-900 dark:text-white">
          Diskon Item
        </h2>
        <p className="mb-4 text-sm text-gray-600 dark:text-white">
          {item.mtrl_name}
        </p>

        <div className="bg-gray-50 dark:bg-[#1b1b1b] rounded-lg p-4 mb-4">
          <div className="flex justify-between mb-2 text-sm">
            <span className="text-gray-600 dark:text-white">Harga Satuan</span>
            <span className="font-medium dark:text-white">
              {formatCurrency(String(item.sell_price))}
            </span>
          </div>
          <div className="flex justify-between mb-2 text-sm">
            <span className="text-gray-600 dark:text-white">Quantity</span>
            <span className="font-medium dark:text-white">{item.qty}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-white">Subtotal Awal</span>
            <span className="font-semibold dark:text-white">
              {formatCurrency(String(originalSubtotal))}
            </span>
          </div>
        </div>

        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
            Tipe Diskon
          </label>
          <div className="grid grid-cols-2 gap-2 dark:text-white">
            <button
              onClick={() => {
                setInputType("percent");
                setDiscountValue("");
              }}
              className={`px-4 py-2.5 rounded-lg font-medium transition ${
                inputType === "percent"
                  ? "bg-[#d7d0fe] text-[#37393d]"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Persen (%)
            </button>
            <button
              onClick={() => {
                setInputType("amount");
                setDiscountValue("");
              }}
              className={`px-4 py-2.5 rounded-lg font-medium transition ${
                inputType === "amount"
                  ? "bg-[#d7d0fe] text-[#37393d]"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Rupiah (Rp)
            </button>
          </div>
        </div>

        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
            Nilai Diskon {inputType === "percent" ? "(%)" : "(Rp)"}
          </label>
          <div className="relative">
            <span className="absolute font-medium text-gray-500 -translate-y-1/2 left-3 top-1/2 dark:text-white">
              {inputType === "percent" ? "%" : "Rp"}
            </span>
            <Input
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              placeholder={
                inputType === "percent" ? "Misal: 10" : "Misal: 5000"
              }
              className="pl-10 dark:text-white"
              autoFocus
            />
          </div>
        </div>

        {value > 0 && (
          <div className="p-4 mb-4 border border-green-200 rounded-lg bg-green-50">
            <div className="flex justify-between mb-2 text-sm">
              <span className="font-medium text-green-700">Potongan</span>
              <span className="font-semibold text-green-700">
                -{formatCurrency(String(calculatedDiscount))}
              </span>
            </div>
            <div className="flex justify-between pt-2 text-base border-t border-green-200">
              <span className="font-semibold text-green-800">
                Subtotal Akhir
              </span>
              <span className="font-bold text-green-800">
                {formatCurrency(String(finalSubtotal))}
              </span>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          {item.discount > 0 && (
            <Button
              variant="destructive"
              className="flex-1 "
              onClick={handleRemoveDiscount}
            >
              Hapus
            </Button>
          )}
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Batal
          </Button>
          <Button
            variant="default"
            className="flex-1 font-semibold"
            onClick={handleSave}
          >
            Simpan
          </Button>
        </div>
      </div>
    </div>
  );
}
